import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Plus, FlaskConical, Search,
  Download, Package, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";
import { PremiumPageGuard } from "../components/PremiumPageGuard";
import { ExerciseForm, ExerciseCard } from "../components/WorkoutLabWidgets";
import { devWarn } from "../utils/devLogger";


// ========== MAIN PAGE ==========
const WorkoutLab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [filter, setFilter] = useState(""); // "" | "physical" | "cognitive"
  const [searchQuery, setSearchQuery] = useState("");
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [importingTemplate, setImportingTemplate] = useState(null);

  const fetchExercises = useCallback(async () => {
    try {
      const url = filter ? `${API}/workoutlab/exercises?exercise_type=${filter}` : `${API}/workoutlab/exercises`;
      const res = await axios.get(url);
      setExercises(res.data.exercises);
    } catch {
      toast.error("Failed to load exercises");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workoutlab/templates`);
      setTemplates(res.data.templates);
    } catch {
      devWarn("Templates fetch failed — using manual exercises only");
    }
  }, []);

  useEffect(() => { fetchExercises(); }, [fetchExercises]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleImportTemplate = async (templateId) => {
    setImportingTemplate(templateId);
    try {
      const res = await axios.post(`${API}/workoutlab/templates/${templateId}/import`);
      toast.success(res.data.message);
      fetchExercises();
      setShowTemplates(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import");
    } finally {
      setImportingTemplate(null);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await axios.post(`${API}/workoutlab/exercises`, formData);
      toast.success(t("workoutLab.created"));
      setShowForm(false);
      fetchExercises();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    }
  };

  const handleUpdate = async (formData) => {
    try {
      await axios.put(`${API}/workoutlab/exercises/${editingExercise.id}`, formData);
      toast.success(t("workoutLab.updated"));
      setEditingExercise(null);
      fetchExercises();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/workoutlab/exercises/${id}`);
      toast.success(t("workoutLab.deleted"));
      fetchExercises();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleAddToPlan = async (exercise) => {
    try {
      await axios.post(`${API}/workoutlab/add-to-plan`, { exercise_id: exercise.id });
      toast.success(t("workoutLab.addedToPlan", { name: exercise.name }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to plan");
    }
  };

  const filteredExercises = exercises.filter(ex =>
    !searchQuery || ex.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const physicalCount = exercises.filter(e => e.exercise_type === "physical").length;
  const cognitiveCount = exercises.filter(e => e.exercise_type === "cognitive").length;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="workoutlab-page">
      <div className="fixed top-0 left-1/4 w-80 h-80 bg-cyan-300/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/3 w-72 h-72 bg-indigo-300/10 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-sky-600 hover:text-sky-800"
            data-testid="workoutlab-back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t("common.dashboard")}
          </Button>
          <Button
            onClick={() => { setShowForm(true); setEditingExercise(null); }}
            className="glow-button"
            data-testid="create-exercise-btn"
          >
            <Plus className="w-4 h-4 mr-1" strokeWidth={2} />
            {t("workoutLab.newExercise")}
          </Button>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 text-cyan-600 text-xs font-semibold uppercase tracking-wider mb-4">
              <FlaskConical className="w-4 h-4" strokeWidth={1.5} />
              {t("workoutLab.subtitle")}
            </div>
            <h1 className="text-3xl sm:text-4xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
              {t("workoutLab.title")}
            </h1>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              {t("workoutLab.description")}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>{exercises.length}</p>
              <p className="text-xs text-slate-400">{t("workoutLab.total")}</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-light text-cyan-600" style={{ fontFamily: "Outfit" }}>{physicalCount}</p>
              <p className="text-xs text-slate-400">{t("workoutLab.physical")}</p>
            </div>
            <div className="glass-card p-4 text-center">
              <p className="text-2xl font-light text-indigo-600" style={{ fontFamily: "Outfit" }}>{cognitiveCount}</p>
              <p className="text-xs text-slate-400">{t("workoutLab.cognitive")}</p>
            </div>
          </div>

          {/* Preset Templates */}
          {templates.length > 0 && (
            <div className="mb-6">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className="w-full glass-card p-4 flex items-center justify-between hover:shadow-md transition-all"
                data-testid="toggle-templates-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-violet-50">
                    <Package className="w-5 h-5 text-violet-500" strokeWidth={1.5} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-sky-800">{t("workoutLab.presetTemplates")}</p>
                    <p className="text-xs text-slate-400">{t("workoutLab.presetDescription")}</p>
                  </div>
                </div>
                {showTemplates ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                )}
              </button>

              {showTemplates && (
                <div className="grid gap-3 mt-3 sm:grid-cols-2 animate-fade-in">
                  {templates.map(tmpl => (
                    <div key={tmpl.id} className="glass-card p-4 border border-violet-100" data-testid={`template-card-${tmpl.id}`}>
                      <h4 className="text-sm font-medium text-sky-800 mb-1" style={{ fontFamily: "Outfit" }}>
                        {tmpl.name}
                      </h4>
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{tmpl.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">{tmpl.exercise_count} {t("workoutLab.exercises")}</span>
                        <Button
                          size="sm"
                          onClick={() => handleImportTemplate(tmpl.id)}
                          disabled={importingTemplate === tmpl.id}
                          className="text-xs bg-violet-500 hover:bg-violet-600 text-white px-3 py-1 h-7"
                          data-testid={`import-template-${tmpl.id}`}
                        >
                          {importingTemplate === tmpl.id ? (
                            <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                          ) : (
                            <>
                              <Download className="w-3 h-3 mr-1" strokeWidth={2} />
                              {t("workoutLab.import")}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Form */}
          {(showForm || editingExercise) && (
            <div className="mb-6">
              <ExerciseForm
                exercise={editingExercise}
                onSave={editingExercise ? handleUpdate : handleCreate}
                onCancel={() => { setShowForm(false); setEditingExercise(null); }}
              />
            </div>
          )}

          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" strokeWidth={1.5} />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t("workoutLab.searchPlaceholder")}
                className="pl-9 border-sky-200 focus:border-sky-400"
                data-testid="exercise-search-input"
              />
            </div>
            <div className="flex gap-2">
              {[
                { val: "", label: t("deepDive.all") },
                { val: "physical", label: t("workoutLab.physical") },
                { val: "cognitive", label: t("workoutLab.cognitive") },
              ].map(f => (
                <button
                  key={f.val}
                  onClick={() => setFilter(f.val)}
                  className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                    filter === f.val ? "bg-sky-400 text-white" : "bg-sky-50 text-sky-600 hover:bg-sky-100"
                  }`}
                  data-testid={`filter-${f.val || "all"}-btn`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Exercise List */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-12 h-12 rounded-full bg-sky-400/30 glow-pulse" />
            </div>
          ) : filteredExercises.length === 0 ? (
            <div className="text-center py-16 glass-card" data-testid="empty-state">
              <FlaskConical className="w-12 h-12 text-sky-300 mx-auto mb-4" strokeWidth={1} />
              <p className="text-sky-800 font-medium mb-1" style={{ fontFamily: "Outfit" }}>
                {searchQuery ? t("workoutLab.noResults") : t("workoutLab.emptyTitle")}
              </p>
              <p className="text-sm text-slate-400 mb-6">
                {searchQuery ? t("workoutLab.tryDifferent") : t("workoutLab.emptyDescription")}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => { setShowForm(true); setEditingExercise(null); }}
                  className="glow-button"
                  data-testid="empty-create-btn"
                >
                  <Plus className="w-4 h-4 mr-1" strokeWidth={2} />
                  {t("workoutLab.createFirst")}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filteredExercises.map(exercise => (
                <ExerciseCard
                  key={exercise.id}
                  exercise={exercise}
                  onEdit={ex => { setEditingExercise(ex); setShowForm(false); }}
                  onDelete={handleDelete}
                  onAddToPlan={handleAddToPlan}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.4s ease-out; }
      `}</style>
    </div>
  );
};

const WorkoutLabGuarded = () => (
  <PremiumPageGuard feature="Workout Lab & Custom Exercises">
    <WorkoutLab />
  </PremiumPageGuard>
);

export default WorkoutLabGuarded;
